import { WebMercatorViewport } from "@deck.gl/core";
import { useMemo } from "react";
import {
  AssetLabel,
  AssetLabelCandidate,
  LABEL_FONT_FAMILY,
  LABEL_FONT_SIZE_PX,
  Rect,
  buildAssetLabelLayout,
  iconRect,
} from "../components/assetLabelLayout";
import {
  AssetLabelBadge,
  BADGE_GAP_PX,
  BADGE_ICON_PX,
  BADGE_TEXT_PADDING_PX,
  badgeItemCount,
  badgePercentText,
} from "../models/assetLabelBadge";

/**
 * A vehicle to put a name on.
 *
 * Deliberately not deck.gl's clustered-feature type: the layout has nothing to do with
 * clustering, and a caller that has already decided which vehicles are drawn individually should
 * not have to hand over the type it happened to hold them in.
 */
export interface LabelPoint {
  deviceId: string;
  coordinates: [number, number];
}

/** Something on the map that carries no name of its own but must not be covered by one. */
export interface LabelObstacle {
  coordinates: [number, number];
  /** Half the obstacle's size on screen, in pixels. */
  halfSizePx: number;
}

/** Where the map is looking, in the shape `WebMercatorViewport` wants. */
export interface LabelViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
}

interface Options {
  /** Pass `false` when names are switched off; nothing is computed then. */
  enabled: boolean;
  /** The vehicles drawn individually — a clustered vehicle has no name of its own. */
  points: LabelPoint[];
  /** Cluster bubbles and anything else a name has to stay clear of. */
  obstacles?: LabelObstacle[];
  /** Smoothed positions, so a name travels with its vehicle instead of jumping. */
  animatedPositions: Map<string, [number, number]>;
  /** Nudges applied to vehicles sitting on top of each other. */
  offsets: Map<string, [number, number]>;
  names: Record<string, string | undefined>;
  /**
   * What else is drawn inside each name box. Needed here, not only by the overlay, because the
   * layout reserves exactly the box that gets drawn and only this hook can measure text.
   */
  badgeById?: ReadonlyMap<string, AssetLabelBadge>;
  viewState: LabelViewState;
  containerSize: { width: number; height: number };
  iconSizePx: number;
}

/**
 * Works out where each vehicle's name goes on screen.
 *
 * Only the vehicles actually on screen are considered. Off-screen ones would take up room in the
 * layout that on-screen names then have to avoid, pushing visible names away from their vehicles
 * for the sake of names nobody can see.
 */
export function useAssetLabels({
  enabled,
  points,
  obstacles,
  animatedPositions,
  offsets,
  names,
  badgeById,
  viewState,
  containerSize,
  iconSizePx,
}: Options): AssetLabel[] {
  const badgeWidthById = useBadgeWidths(badgeById);

  return useMemo(() => {
    const { width, height } = containerSize;
    // A viewport of no size projects everything to the same spot, which would put every name in
    // one pile. Nothing is drawn on the first frame anyway, so wait for the real size rather than
    // lay out against a made-up one.
    if (!enabled || points.length === 0 || width === 0 || height === 0) {
      return [];
    }

    const viewport = new WebMercatorViewport({ width, height, ...viewState });
    const iconHalfPx = Math.max(8, Math.round(iconSizePx / 2));
    const measureTextWidth = createTextMeasurer();

    /** Rectangles that block a name without getting one: cluster bubbles, unnamed vehicles. */
    const blockers: Rect[] = [];
    const candidates: AssetLabelCandidate[] = [];

    for (const obstacle of obstacles ?? []) {
      const projected = projectOnScreen(viewport, obstacle.coordinates, width, height);
      if (projected) {
        blockers.push(
          iconRect({
            anchorX: projected[0],
            anchorY: projected[1],
            iconHalfPx: obstacle.halfSizePx,
          }),
        );
      }
    }

    for (const { deviceId, coordinates } of points) {
      const base = animatedPositions.get(deviceId) ?? coordinates;
      const offset = offsets.get(deviceId);
      const position: [number, number] = offset ? [base[0] + offset[0], base[1] + offset[1]] : base;

      const projected = projectOnScreen(viewport, position, width, height);
      if (!projected) {
        continue;
      }

      const anchor = { anchorX: projected[0], anchorY: projected[1], iconHalfPx };
      const text = names[deviceId];
      // A vehicle whose name has not arrived yet gets no box — its internal id would be shown to
      // the reader as if it were the name. Its icon still has to be kept clear.
      if (!text) {
        blockers.push(iconRect(anchor));
        continue;
      }

      candidates.push({
        id: deviceId,
        text,
        ...anchor,
        extraWidth: badgeWidthById.get(deviceId) ?? 0,
      });
    }

    return buildAssetLabelLayout(candidates, width, height, { blockers, measureTextWidth });
  }, [
    enabled,
    points,
    obstacles,
    animatedPositions,
    offsets,
    names,
    badgeWidthById,
    viewState,
    containerSize,
    iconSizePx,
  ]);
}

/**
 * Room the badges take up beside each name, in pixels.
 *
 * Worked out on its own rather than inside the layout, because the layout runs on every animation
 * frame while anything moves and a badge only changes when the status poll comes back.
 */
function useBadgeWidths(
  badgeById: ReadonlyMap<string, AssetLabelBadge> | undefined,
): ReadonlyMap<string, number> {
  return useMemo(() => {
    const widths = new globalThis.Map<string, number>();
    if (!badgeById) {
      return widths;
    }

    const measureTextWidth = createTextMeasurer();
    for (const [deviceId, badge] of badgeById) {
      widths.set(deviceId, badgeWidth(badge, measureTextWidth));
    }
    return widths;
  }, [badgeById]);
}

/**
 * Room the badges take up beside a name, in pixels.
 *
 * Has to agree with what `AssetLabelOverlay` draws, down to the gaps between the items — the
 * layout reserves this width and the box is then drawn at exactly that size, so an error here
 * shows up as a clipped name or a name sitting off-centre over its vehicle.
 */
function badgeWidth(badge: AssetLabelBadge, measureTextWidth?: (text: string) => number): number {
  const hasLevel = badge.energyPercent !== undefined;
  if (!badge.isCharging && !hasLevel && !badge.powertrainLabel) {
    return 0;
  }

  const percentText = badgePercentText(badge);
  const percentWidth = measureTextWidth
    ? measureTextWidth(percentText)
    : percentText.length * FALLBACK_CHARACTER_WIDTH;

  const powertrainWidth = badge.powertrainLabel
    ? (measureTextWidth
        ? measureTextWidth(badge.powertrainLabel)
        : badge.powertrainLabel.length * FALLBACK_CHARACTER_WIDTH) +
      // Its own padding and border, which the chip is drawn with.
      BADGE_TEXT_PADDING_PX * 2 +
      2
    : 0;

  return (
    (badgeItemCount(badge) - 1) * BADGE_GAP_PX +
    powertrainWidth +
    (badge.isCharging ? BADGE_ICON_PX : 0) +
    (hasLevel ? BADGE_ICON_PX : 0) +
    percentWidth
  );
}

/**
 * Screen position of a point, or `null` when it is not on screen.
 *
 * Points behind the camera are dropped too. With the map tilted, `project` mirrors those into the
 * visible range instead of returning something out of range, which would put a name in the middle
 * of the map with a line pointing at nothing.
 */
function projectOnScreen(
  viewport: WebMercatorViewport,
  position: [number, number],
  width: number,
  height: number,
): [number, number] | null {
  const [x, y, z] = viewport.project([position[0], position[1], 0]);
  if (z > 1) {
    return null;
  }
  if (x < 0 || x > width || y < 0 || y > height) {
    return null;
  }
  return [x, y];
}

/** Only reached when the browser gives us no canvas; the layout uses the same rough guess. */
const FALLBACK_CHARACTER_WIDTH = 7.5;

const textWidthCache = new globalThis.Map<string, number>();
/** `undefined` until looked up, `null` when the browser gives us no canvas to measure with. */
let measureContext: CanvasRenderingContext2D | null | undefined;

/**
 * Measures a name in the font it is drawn in.
 *
 * Guessing from the character count is out by enough to matter: an estimate too small clips the
 * name, one too large leaves it sitting off-centre in its box.
 */
function createTextMeasurer(): ((text: string) => number) | undefined {
  if (measureContext === undefined) {
    measureContext = document.createElement("canvas").getContext("2d");
    if (measureContext) {
      measureContext.font = `${LABEL_FONT_SIZE_PX}px ${LABEL_FONT_FAMILY}`;
    }
  }

  const context = measureContext;
  if (!context) {
    return undefined;
  }

  return (text: string) => {
    const cached = textWidthCache.get(text);
    if (cached !== undefined) {
      return cached;
    }

    const measured = context.measureText(text).width;
    textWidthCache.set(text, measured);
    return measured;
  };
}
